/**
 * NotificationCenter - Persistent Notification Dropdown
 * 
 * Displays all persistent notifications in a dropdown panel:
 * - Grouped by date
 * - Mark as read
 * - Dismiss individual or all
 * - Links to entity detail via actionUrl or pluginData.url
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications, type PersistentNotification } from '../../contexts/NotificationContext';
import { resolveEntityLink } from '../../plugins/registries/entity-link-resolver';
import './NotificationCenter.css';

// ================================
// Icons
// ================================

const typeIcons: Record<string, string> = {
  info: 'ℹ️',
  success: '✅',
  warning: '⚠️',
  error: '❌',
  insight: '💡',
};

// ================================
// Notification Item
// ================================

interface NotificationItemProps {
  notification: PersistentNotification;
  onRead: () => void;
  onDismiss: () => void;
  onClick: () => void;
}

function NotificationItem({ notification, onRead, onDismiss, onClick }: NotificationItemProps) {
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  return (
    <div 
      className={`notification-item ${notification.read ? 'notification-item--read' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="notification-item__icon">
        {typeIcons[notification.type] || typeIcons.info}
      </div>

      <div className="notification-item__content">
        <div className="notification-item__title">{notification.title}</div>
        {notification.message && (
          <div className="notification-item__message">{notification.message}</div>
        )}
        <div className="notification-item__meta">
          <span className="notification-item__time">{timeAgo}</span>
          {notification.pluginData && (
            <span className="notification-item__type">
              {notification.pluginData.title}
            </span>
          )}
        </div>
      </div>

      <div className="notification-item__actions">
        {!notification.read && (
          <button 
            className="notification-item__mark-read"
            onClick={(e) => { e.stopPropagation(); onRead(); }}
            title="Mark as read"
          >
            ✓
          </button>
        )}
        <button 
          className="notification-item__dismiss"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          title="Dismiss"
        >
          ×
        </button>
      </div>

      {!notification.read && (
        <div className="notification-item__unread-indicator" />
      )}
    </div>
  );
}

// ================================
// Empty State
// ================================

function EmptyState() {
  return (
    <div className="notification-center__empty">
      <div className="notification-center__empty-icon">🔔</div>
      <div className="notification-center__empty-text">No notifications</div>
      <div className="notification-center__empty-subtext">
        You're all caught up!
      </div>
    </div>
  );
}

// ================================
// Notification Center
// ================================

export function NotificationCenter() {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const { 
    state, 
    closeCenter, 
    markAsRead, 
    markAllAsRead, 
    dismissNotification, 
    clearAll 
  } = useNotifications();

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        closeCenter();
      }
    }

    if (state.isCenterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [state.isCenterOpen, closeCenter]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeCenter();
      }
    }

    if (state.isCenterOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [state.isCenterOpen, closeCenter]);

  if (!state.isCenterOpen) return null;

  const handleNotificationClick = (notification: PersistentNotification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.pluginData) {
      const targetUrl = resolveEntityLink({
        explicitUrl: notification.pluginData.url,
        entityType: notification.pluginData.entityType,
        entityId: notification.pluginData.id,
        fallbackUrl: notification.actionUrl,
      });
      if (targetUrl) {
        navigate(targetUrl);
        closeCenter();
      }
    } else if (notification.actionUrl) {
      navigate(notification.actionUrl);
      closeCenter();
    }
  };

  return (
    <div className="notification-center" ref={panelRef}>
      {/* Header */}
      <div className="notification-center__header">
        <div className="notification-center__title">
          Notifications
          {state.unreadCount > 0 && (
            <span className="notification-center__count">{state.unreadCount}</span>
          )}
        </div>
        <div className="notification-center__header-actions">
          {state.unreadCount > 0 && (
            <button 
              className="notification-center__mark-all"
              onClick={markAllAsRead}
            >
              Mark all read
            </button>
          )}
          {state.notifications.length > 0 && (
            <button 
              className="notification-center__clear-all"
              onClick={clearAll}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="notification-center__list">
        {state.notifications.length === 0 ? (
          <EmptyState />
        ) : (
          state.notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={() => markAsRead(notification.id)}
              onDismiss={() => dismissNotification(notification.id)}
              onClick={() => handleNotificationClick(notification)}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {state.notifications.length > 0 && (
        <div className="notification-center__footer">
          <button 
            className="notification-center__view-all"
            onClick={() => { navigate('/notifications'); closeCenter(); }}
          >
            View all notifications →
          </button>
        </div>
      )}
    </div>
  );
}

// ================================
// Notification Bell Button
// ================================

interface NotificationBellProps {
  className?: string;
}

export function NotificationBell({ className }: NotificationBellProps) {
  const { state, toggleCenter } = useNotifications();

  return (
    <button 
      className={`notification-bell ${className || ''}`}
      onClick={toggleCenter}
      aria-label={`Notifications ${state.unreadCount > 0 ? `(${state.unreadCount} unread)` : ''}`}
    >
      <span className="notification-bell__icon">🔔</span>
      {state.unreadCount > 0 && (
        <span className="notification-bell__badge">
          {state.unreadCount > 99 ? '99+' : state.unreadCount}
        </span>
      )}
    </button>
  );
}

export default NotificationCenter;
